import { ConsumptionInformationNorlys } from '~src/components/form/Information/Consumption';
import { CustomerInformationNorlys } from '~src/components/form/Information/Customer';
import { HeatingInformationNorlys } from '~src/components/form/Information/Heating';
import { ResidenceInformationNorlys } from '~src/components/form/Information/Residence';
import { WelcomeInformationNorlys } from '~src/components/form/Information/Welcome/norlys';
import { IconConstructor } from '~src/components/Icon';
import { HeatPumpInformationDesktopNorlys } from '~src/components/Products/Information/heatPump/desktop';
import { useCustomerContext } from '~src/context/CustomerContext';

import { usePublicCalculatorContext } from '~src/context/PublicCalculatorContext';
import { customerDetailsFilledOut } from '~src/hooks/graphql/useOffer';
import { HeatPumpProducts, Residence } from '~src/model';
import {
  calculateHeatPumpScores,
  calculateResidenceHeatingRequired,
  findBestCompatibleIndoorUnit,
  isResidenceConsumptionFilled,
  isResidenceInfoFilled,
} from '~src/util';

type CalculatorStep = {
  content: React.JSX.Element;
  Icon?: IconConstructor;
  title: string;
  key: string;
  completed?: boolean;
};

export const useInformationPageStepsNorlys = (isMobile = false): CalculatorStep[] => {
  const { residence, setVisitedStageMap, setOpenStageMap, offerProducts, indoorUnits, outdoorUnits, setOfferProducts } =
    usePublicCalculatorContext();

  const { customer } = useCustomerContext();

  const { heatPumpIndoorUnit, heatPumpOutdoorUnit } = offerProducts ?? {};

  const selectedOutdoorUnitID = heatPumpOutdoorUnit?.id;

  const heatingRequired = residence?.primaryHeating ? calculateResidenceHeatingRequired(residence ?? {}) : 0;
  const heatPumpProducts = calculateHeatPumpScores(outdoorUnits ?? [], residence as Residence, heatingRequired)
    .available?.map(outdoorUnit => ({
      heatPumpOutdoorUnit: outdoorUnit,
      heatPumpIndoorUnit: findBestCompatibleIndoorUnit(outdoorUnit, indoorUnits),
    }))
    .filter(unit => !!unit.heatPumpIndoorUnit) as HeatPumpProducts[];

  const welcomeStep = {
    title: 'Velkommen til Bodils Energiberegner',
    content: (
      <WelcomeInformationNorlys
        {...{
          onProceed: () => {
            setVisitedStageMap?.(1);
            setOpenStageMap?.({ 2: true });
          },
        }}
      />
    ),
    key: 'introduction',
    completed: true,
  };

  const residenceStep = {
    content: <ResidenceInformationNorlys />,
    title: 'Boligdata',
    key: 'residenceInformation',
    completed: isResidenceInfoFilled(residence) && !!residence?.areaHeated,
  };

  const heatingStep = {
    content: <HeatingInformationNorlys />,
    title: 'Boligens opvarming',
    key: 'heatingInformation',
    completed: !!residence?.heatDistribution,
  };

  const consumptionStep = {
    content: <ConsumptionInformationNorlys />,
    title: 'Forbrugsdata',
    key: 'consumptionInformation',
    completed: isResidenceConsumptionFilled(residence),
  };

  const productStep = {
    content: (
      <HeatPumpInformationDesktopNorlys
        heatPumpProducts={heatPumpProducts}
        selectedOutdoorUnitID={selectedOutdoorUnitID}
        onClick={(heatPumps: HeatPumpProducts) => {
          const { heatPumpOutdoorUnit: newOutdoorUnit, heatPumpIndoorUnit: newIndoorUnit } = heatPumps;

          if (!newOutdoorUnit || !newIndoorUnit) {
            return;
          }

          if (newOutdoorUnit.id === selectedOutdoorUnitID) {
            setOfferProducts?.({
              ...offerProducts,
              heatPumpOutdoorUnit: undefined,
              heatPumpIndoorUnit: undefined,
            });
            return;
          }

          setOfferProducts?.({
            ...offerProducts,
            heatPumpOutdoorUnit: newOutdoorUnit,
            heatPumpIndoorUnit: newIndoorUnit,
          });
        }}
      />
    ),
    title: 'Vælg produkter',
    key: 'productInformation',
    completed: !!heatPumpOutdoorUnit && !!heatPumpIndoorUnit,
  };

  const finishStep = {
    content: <CustomerInformationNorlys />,
    title: 'Udfyld oplysninger',
    key: 'customerInformation',
    completed: customerDetailsFilledOut(customer ?? {}),
  };

  if (isMobile) {
    return [residenceStep, heatingStep, consumptionStep, productStep, finishStep];
  }

  return [welcomeStep, residenceStep, heatingStep, consumptionStep, productStep];
};
